import React, {useEffect, useMemo, useState} from "react";
import "./Filtration.css";
import Header from "../../../components/common/Header/Header";
import {useParams} from "react-router-dom";
import {AppDispatch} from "../../../redux/store";
import {useDispatch, useSelector} from "react-redux";
import RootState from "../../../redux/states/root.state";
import {fetchSpa} from "../../../redux/actions/spa.actions";
import useMQTT from "../../../hooks/useMQTT";
import Command from "../../../classes/command.class";
import Stepper from "../../../components/common/Stepper/Stepper";
import Toggle from "../../../components/common/Toggle/Toggle";
import { inputDebounce, settingsDebounce } from "../debounceTimes";
import { SpaLiveLabels } from "../../../mqttData/live/spaLiveLabels.enums";
import { commandKeys } from "../../../mqttData/commands/commands.enum";
import { filterStateInfo } from "../../../mqttData/live/filterState";

// How long "Starting…" shows after Run Filter Cycle before giving up on the
// spa reporting a new state.
const cycleStartTimeout = 5000;


const Filtration = () => {
    // Configs:
    const { id } = useParams();
    const dispatch: AppDispatch = useDispatch();

    // SPA:
    const spa = useSelector((state: RootState) => state.spa.data);
    const isConnected = useSelector((state: RootState) => 
        state.spa.currentSpaId === id && state.spa.isCurrentSpaConnected
    );
    const isConnecting = useSelector((state: RootState) => 
        state.spa.currentSpaId === id && state.spa.isConnecting
    );

    const settingsData = useSelector((state: RootState) => state.settings.data);
    const constData = useSelector((state: RootState) => state.const.data);
    const liveData = useSelector((state: RootState) => state.live.data);

    // MQTT Functionality:
    const [ commandTopic] = useMQTT(id ?? '')
    const command = new Command(commandTopic)

    /* Filter status and the manual cycle */
    // The live reducer keeps the raw payload, so this is the spa's integer
    // state (the model's boolean type predates the state table).
    const filterState = liveData[SpaLiveLabels.Filter] as unknown as number | undefined;
    const filterInfo = filterStateInfo(filterState);
    // Set when Run Filter Cycle is pressed; cleared when the spa reports a new
    // state, or on the timeout. Only a change confirms the cycle took: live
    // messages come every few seconds and would otherwise re-enable the button
    // before the pump had started.
    const [cycleRequestedAt, setCycleRequestedAt] = useState<number | null>(null);
    useEffect(() => {
        if (cycleRequestedAt === null) return;
        const timer = setTimeout(() => setCycleRequestedAt(null), cycleStartTimeout);
        return () => clearTimeout(timer);
    }, [cycleRequestedAt]);
    useEffect(() => {
        setCycleRequestedAt(null);
    }, [filterState]);
    const cycleStarting = cycleRequestedAt !== null;
    const handleRunFilterCycle = () => {
        // An explicit 1, the way the old apps sent it. Their stop (0) is noted
        // in their source as doing nothing, so there is no stop here.
        command.executeSingle(commandKeys.FLTRBoost, 1);
        setCycleRequestedAt(Date.now());
    };

    /* Filters functionality */
    const [stopFilterAbove3, setStopFilterAbove3] = useState(false)
    const [filterFrequencey, setFilterFrequencey] = useState(0)
    const [filterDuration, setfilterDuration] = useState(0)

    // Debounce logic states
    const [lastUserFD, setLastUserFD] = useState<number | null>(null);
    const [lastUpdateFD, setLastUpdateFD] = useState<number>(0);
    const [lastUserFF, setLastUserFF] = useState<number | null>(null);
    const [lastUpdateFF, setLastUpdateFF] = useState<number>(0);
    const [lastUserFS, setLastUserFS] = useState<boolean | null>(null);
    const [lastUpdateFS, setLastUpdateFS] = useState<number>(0);

    // Fetch SPA From the DB
    useEffect(() => {
        if (id != null) {
            dispatch(fetchSpa(id));
        }
    }, [dispatch, id]);

    // Sync local state with backend for filterDuration
    useEffect(() => {
        if (settingsData.FD !== undefined) {
            const now = Date.now();
            const backendFD = settingsData.FD as number;
            // Only update if user is NOT actively changing (lastUserFD is null) AND debounce window expired
            if (
                lastUserFD === null &&
                now - lastUpdateFD > settingsDebounce
            ) {
                setfilterDuration(backendFD);
            }
            // If backend confirms the last user value, clear lastUserFD
            if (lastUserFD !== null && backendFD === lastUserFD) {
                setLastUserFD(null);
            }
        }
    }, [settingsData.FD]);

    // Debounced effect to send filterDuration
    useEffect(() => {
        if (lastUserFD !== null && filterDuration === lastUserFD) {
            const timer = setTimeout(() => {
                command.updateSettings({ setFD: lastUserFD });
            }, inputDebounce);
            return () => clearTimeout(timer);
        }
    }, [lastUserFD, filterDuration]);

    // Sync local state with backend for filterFrequencey
    useEffect(() => {
        if (settingsData.FF !== undefined) {
            const now = Date.now();
            const backendFF = settingsData.FF as number;
            if (
                lastUserFF === null &&
                now - lastUpdateFF > settingsDebounce
            ) {
                setFilterFrequencey(backendFF);
            }
            if (lastUserFF !== null && backendFF === lastUserFF) {
                setLastUserFF(null);
            }
        }
    }, [settingsData.FF]);

    // Debounced effect to send filterFrequencey
    useEffect(() => {
        if (lastUserFF !== null && filterFrequencey === lastUserFF) {
            const timer = setTimeout(() => {
                command.updateSettings({ setFF: lastUserFF });
            }, inputDebounce);
            return () => clearTimeout(timer);
        }
    }, [lastUserFF, filterFrequencey]);

    // Sync local state with backend for stopFilterAbove3
    useEffect(() => {
        if (settingsData.FS !== undefined) {
            const now = Date.now();
            const backendFS = settingsData.FS as boolean;
            if (
                lastUserFS === null &&
                now - lastUpdateFS > settingsDebounce
            ) {
                setStopFilterAbove3(backendFS);
            }
            if (lastUserFS !== null && backendFS === lastUserFS) {
                setLastUserFS(null);
            }
        }
    }, [settingsData.FS]);

    // Debounced effect to send stopFilterAbove3
    useEffect(() => {
        if (lastUserFS !== null && stopFilterAbove3 === lastUserFS) {
            const timer = setTimeout(() => {
                command.updateSettings({ setFS: lastUserFS });
            }, inputDebounce);
            return () => clearTimeout(timer);
        }
    }, [lastUserFS, stopFilterAbove3]);

    // Handlers for user input (match SpaControlPage)
    const handleFilterDurationChange = (value: number) => {
        setfilterDuration(value);
        setLastUserFD(value);
        setLastUpdateFD(Date.now());
    };
    const handleFilterFrequencyChange = (value: number) => {
        setFilterFrequencey(value);
        setLastUserFF(value);
        setLastUpdateFF(Date.now());
    };
    const handleStopFilterChange = (value: boolean) => {
        setStopFilterAbove3(value);
        setLastUserFS(value);
        setLastUpdateFS(Date.now());
    };

    const totalHours = filterDuration * filterFrequencey;
    const isOffline = !isConnected && !isConnecting;
    const controlsDisabled = !isConnected;

    return (
        <div className="page filtration-page">
            <Header/>
            {isConnecting ?
            <div className="filtration-loading"><span className="spinner" role="progressbar" aria-label="Loading"/></div>
            :
            <main className={`filtration-main ${isOffline ? 'offline' : ''}`}>
                {isOffline && <div className="page-notice">Spa is offline</div>}

                {/* First, as on the app: what the pump is doing now, before the
                    settings that govern it. */}
                <div className="filter-status">
                    <div className="filter-status-text">
                        <span className="filter-status-title">Filter Status</span>
                        <span className="filter-status-desc">What the filter pump is doing right now</span>
                    </div>
                    <span
                        className={`chip filter-status-chip ${filterInfo.tone}`}
                        role="status"
                        aria-label={`Filter ${filterInfo.label === '—' ? 'state unknown' : filterInfo.label.toLowerCase()}`}
                    >
                        {filterInfo.label}
                    </span>
                </div>

                <div className="filtration-steppers">
                    <Stepper
                        label="Duration"
                        value={filterDuration}
                        unit={filterDuration === 1 ? 'Hour' : 'Hours'}
                        onDecrement={() => handleFilterDurationChange(filterDuration - 1)}
                        onIncrement={() => handleFilterDurationChange(filterDuration + 1)}
                        decrementDisabled={filterDuration <= (constData.setFDmin ?? 0) || controlsDisabled}
                        incrementDisabled={filterDuration >= (constData.setFDmax ?? 2) || controlsDisabled}
                    />
                    <Stepper
                        label="Frequency"
                        value={filterFrequencey}
                        unit="Times a Day"
                        onDecrement={() => handleFilterFrequencyChange(filterFrequencey - 1)}
                        onIncrement={() => handleFilterFrequencyChange(filterFrequencey + 1)}
                        decrementDisabled={filterFrequencey <= (constData.setFFmin ?? 0) || controlsDisabled}
                        incrementDisabled={filterFrequencey >= (constData.setFFmax ?? 12) || controlsDisabled}
                    />
                </div>

                <p className="filter-summary">
                    Filter will run for <strong>{totalHours} {totalHours === 1 ? 'hour' : 'hours'}</strong> every day
                </p>

                <div className="filter-suspension">
                    <div className="filter-suspension-text">
                        <span className="filter-suspension-title">Filter Suspension</span>
                        <span className="filter-suspension-desc">Pause filtering when the water is 3° above the set temperature</span>
                    </div>
                    <Toggle
                        label="Filter Suspension"
                        checked={stopFilterAbove3}
                        onChange={handleStopFilterChange}
                        disabled={controlsDisabled}
                    />
                </div>

                {/* Same rules as the app: a cycle already running reports itself
                    on the button instead of offering a start that would do
                    nothing, which also covers one started from the panel. */}
                <div className="filter-cycle">
                    <div className="filter-cycle-text">
                        <span className="filter-cycle-title">Run Filter Cycle</span>
                        <span className="filter-cycle-desc">
                            Start a cycle now, on top of the daily schedule. It runs its normal length and stops on its own.
                        </span>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary filter-cycle-button"
                        disabled={controlsDisabled || cycleStarting || !filterInfo.canStart}
                        aria-busy={cycleStarting || filterInfo.running}
                        onClick={handleRunFilterCycle}
                    >
                        {cycleStarting ? 'Starting…' : filterInfo.running ? `${filterInfo.label}…` : 'Run Filter Cycle'}
                    </button>
                </div>
            </main>
            }
        </div>
    );
};

export default Filtration;
